import React, { useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  AttachMoney,
  AutoAwesome,
  Check,
  CheckCircleOutline,
  Close,
  Description,
  ExpandLess,
  ExpandMore,
  Info,
  LocalShipping,
  OpenInNew,
  Person,
  PlayArrow,
  StopCircleOutlined,
} from '@mui/icons-material';
import { blue, blueGrey, green, orange } from '@mui/material/colors';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { useSubprimeAgentSessionUpdated } from 'hooks/useSubscriptions';
import { START_SUBPRIME_AGENT_SESSION } from '../../api/startSubprimeAgent';
import { GET_SUBPRIME_AGENT_SESSIONS } from '../../api/getSubprimeAgentSessions';

interface DetailItem {
  label: string;
  subtitle: string;
  Icon: React.ElementType;
}

interface SubprimeAgentSession {
  id: string;
  sessionId: string;
  status: string;
  wantsFinancing: boolean | null;
  downpaymentFunds: number | null;
  collateral: string | null;
  guarantor: string | null;
  notes: string | null;
  name: string | null;
  phone: string | null;
  completedAt: string | null;
  stoppedByDealer: boolean;
  optedOut: boolean;
  createdAt: string;
  modifiedAt: string;
}

const buildDetailItems = (session: SubprimeAgentSession | null): DetailItem[] => [
  {
    label: 'Viability',
    subtitle:
      session?.wantsFinancing === true
        ? 'Wants financing'
        : session?.wantsFinancing === false
          ? 'Does not want financing'
          : 'Not evaluated yet',
    Icon: Check,
  },
  {
    label: 'Summary',
    subtitle: session?.notes ?? 'Will be generated',
    Icon: Description,
  },
  {
    label: 'Down Payment',
    subtitle: session?.downpaymentFunds != null ? `$${session.downpaymentFunds.toLocaleString()}` : 'Not collected',
    Icon: AttachMoney,
  },
  {
    label: 'Collateral',
    subtitle: session?.collateral ?? 'Not provided',
    Icon: LocalShipping,
  },
  {
    label: 'Guarantor',
    subtitle: session?.guarantor ?? 'Not discussed',
    Icon: Person,
  },
];

const REQUIRES_CONFIG_ITEMS = ['Compliance Assistant', 'Credit Evaluation Assistant', 'Protect IQ Assistant'];

const ASSISTANT_DESCRIPTION =
  'Automatically collect qualification details from the applicant and identify whether the deal has real funding potential';

interface StatusBadgeConfig {
  label: string;
  Icon: React.ElementType;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

const getStatusBadge = (session: SubprimeAgentSession | null, loading: boolean): StatusBadgeConfig => {
  if (loading) {
    return { label: '...', Icon: Info, bgColor: blueGrey[50], borderColor: blueGrey[100], textColor: blueGrey[600] };
  }
  if (!session) {
    return {
      label: 'Not Started',
      Icon: Close,
      bgColor: blueGrey[50],
      borderColor: blueGrey[100],
      textColor: blueGrey[600],
    };
  }
  switch (session.status) {
    case 'completed':
      return {
        label: 'Completed',
        Icon: CheckCircleOutline,
        bgColor: green[50],
        borderColor: green[200],
        textColor: green[800],
      };
    case 'opted_out':
      return {
        label: 'Opted Out',
        Icon: StopCircleOutlined,
        bgColor: orange[50],
        borderColor: orange[200],
        textColor: orange[800],
      };
    case 'stopped':
      return {
        label: 'Stopped',
        Icon: StopCircleOutlined,
        bgColor: blueGrey[50],
        borderColor: blueGrey[100],
        textColor: blueGrey[600],
      };
    default:
      return { label: 'In Progress', Icon: PlayArrow, bgColor: blue[50], borderColor: blue[200], textColor: blue[800] };
  }
};

export const StartSubprimeAgentDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.StartSubprimeAgentDialog);
  const { showNotification } = useNotifications();
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [demoMode, setDemoMode] = useState(true);

  const {
    data: sessionsData,
    loading: sessionsLoading,
    refetch: refetchSessions,
  } = useQuery(GET_SUBPRIME_AGENT_SESSIONS, {
    variables: { vendorOpportunityId: data?.voId },
    skip: !isOpen || !data?.voId,
    fetchPolicy: 'cache-and-network',
  });

  useSubprimeAgentSessionUpdated(isOpen ? (data?.voId ?? null) : null, refetchSessions);

  const latestSession: SubprimeAgentSession | null = sessionsData?.subprimeAgent?.getSessionsByVo?.[0] ?? null;

  const detailItems = buildDetailItems(latestSession);

  const statusBadge = getStatusBadge(latestSession, sessionsLoading);
  const hasSession = latestSession !== null;

  const [startSession, { loading }] = useLazyQuery(START_SUBPRIME_AGENT_SESSION, {
    fetchPolicy: 'network-only',
  });

  const handleOpenMessages = () => {
    handleClose();
    data?.onOpenAgentConversation?.();
  };

  const handleStartAssistant = async () => {
    const result = await startSession({
      variables: {
        input: {
          vendorOpportunityId: data?.voId,
          demoMode,
        },
      },
    });

    const response = result.data?.subprimeAgent?.startSession;

    if (response?.success) {
      showNotification('AI Agent session started successfully', { type: 'success' });
      handleClose();
    } else {
      showNotification(response?.message ?? 'Failed to start AI Agent session', { type: 'error' });
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          width: 400,
          height: '100%',
          bgcolor: blueGrey[50],
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          p: '8px',
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            pt: 3,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: '0 0 4px 4px',
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(137.68deg, #2196f3 10.79%, #7AE7E5 94.57%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AutoAwesome sx={{ fontSize: 18, color: 'white' }} />
          </Box>

          <Typography variant="h6" sx={{ flex: 1 }}>
            AI Assistant
          </Typography>

          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Paper>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'white',
            p: 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Available section */}
          <Stack gap={1}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '22px', letterSpacing: '0.1px' }}>
              Available
            </Typography>

            {/* Qualification Assistant card */}
            <Box
              sx={{
                border: '1px solid #2196f3',
                borderRadius: 2,
                pb: 1.5,
                pt: 1,
                px: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              {/* Title row */}
              <Stack
                direction="row"
                gap={1}
                alignItems="flex-start"
                sx={{ borderBottom: '1px solid', borderColor: 'grey.200', py: 0.5 }}
              >
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  Qualification Assistant
                </Typography>

                {/* Status badge */}
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    bgcolor: statusBadge.bgColor,
                    border: '1px solid',
                    borderColor: statusBadge.borderColor,
                    borderRadius: '4px',
                    px: 1,
                    py: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <statusBadge.Icon sx={{ fontSize: 14, color: statusBadge.textColor }} />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '14px',
                      letterSpacing: '0.15px',
                      color: statusBadge.textColor,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {statusBadge.label}
                  </Typography>
                </Stack>
              </Stack>

              {/* Description */}
              <Typography variant="body2" color="text.secondary">
                {ASSISTANT_DESCRIPTION}
              </Typography>

              {/* Start button + Demo Mode checkbox */}
              {(!hasSession || latestSession?.status !== 'in_progress') && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<PlayArrow />}
                    disabled={loading}
                    onClick={handleStartAssistant}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Start Assistant
                  </Button>

                  <FormControlLabel
                    label="Demo Mode"
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    control={<Checkbox size="small" checked={demoMode} onChange={e => setDemoMode(e.target.checked)} />}
                  />
                </Stack>
              )}

              {/* Details collapsible */}
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: blueGrey[100],
                  pt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle2">Details</Typography>
                    <IconButton size="small" sx={{ p: 0 }} onClick={() => setDetailsOpen(prev => !prev)}>
                      {detailsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                    {sessionsLoading && <CircularProgress size={12} />}
                  </Stack>

                  {hasSession && (
                    <Button
                      variant="text"
                      size="small"
                      color="primary"
                      startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                      onClick={handleOpenMessages}
                      sx={{ textTransform: 'uppercase', py: 0, minWidth: 0 }}
                    >
                      Open Messages
                    </Button>
                  )}
                </Stack>

                {detailsOpen && (
                  <Box>
                    {/* Info row */}
                    <Stack direction="row" gap={0.5} alignItems="flex-start" mb={0.5}>
                      <Info sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                      <Typography variant="body2" color="primary">
                        {hasSession
                          ? 'Qualification details collected by the assistant'
                          : 'Qualification details the assistant will collect'}
                      </Typography>
                    </Stack>

                    {/* Detail items */}
                    {detailItems.map(({ label, subtitle, Icon }, index) => (
                      <Stack
                        key={label}
                        direction="row"
                        gap={1}
                        alignItems="flex-start"
                        sx={{
                          py: 1.5,
                          ...(index < detailItems.length - 1 && {
                            borderBottom: '1px solid',
                            borderColor: 'grey.200',
                          }),
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: blue[50],
                            borderRadius: '4px',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ fontSize: 22, color: 'primary.main' }} />
                        </Box>
                        <Stack gap={0.5}>
                          <Typography variant="subtitle2">{label}</Typography>
                          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                            {subtitle}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>

          {/* Requires Configuration section */}
          <Stack gap={1}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '22px', letterSpacing: '0.1px' }}>
              Requires Configuration
            </Typography>

            <Stack gap={0.5}>
              {REQUIRES_CONFIG_ITEMS.map(name => (
                <Box
                  key={name}
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    pb: 1.5,
                    pt: 1,
                    px: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.200', py: 0.5 }}>
                    <Typography variant="subtitle2" color="text.disabled">
                      {name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.disabled">
                    {ASSISTANT_DESCRIPTION}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
